import React, { useEffect, useState } from 'react';
import { Snackbar, Alert, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import eventBus from '../../utils/eventBus';
import { useHelpBundle } from '../help/helpBundle';

// Mounted once inside the router. Any 402 ENTITLEMENT_DENIED the axios
// interceptor sees ends up here as a single, consistent message with a
// "See plans" action — pages don't have to handle 402 individually.
//
// For a plan-limit denial (`reason: 'limit_reached'`), the message is built
// from the same help.json `planLimit` dictionary as the meter's popover and
// the panel's limit article (step 9) — "<Feature> limit reached for your
// plan" — instead of the server's raw message, so all three read identically.
// Every other denial reason keeps showing the server's own message, unchanged.
const EntitlementSnackbar = () => {
  const { t } = useTranslation();
  const helpBundle = useHelpBundle();
  const navigate = useNavigate();
  const [denied, setDenied] = useState(null);

  useEffect(() => eventBus.on(eventBus.EVENTS.ENTITLEMENT_DENIED, (detail) => setDenied(detail || {})), []);

  const isAdmin = localStorage.getItem('userRole') === 'admin';
  const close = () => setDenied(null);

  const featureLabel = denied?.feature ? helpBundle.planLimit?.featureName?.[denied.feature] : null;
  const message = denied?.reason === 'limit_reached' && featureLabel
    ? t('help:planLimit.snackbarLimitReached', { feature: featureLabel, defaultValue: `${featureLabel} limit reached for your plan` })
    : (denied?.error || t('This action is not included in your plan', { defaultValue: 'This action is not included in your plan' }));

  return (
    <Snackbar
      open={Boolean(denied)}
      autoHideDuration={8000}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity="warning"
        onClose={close}
        action={isAdmin ? (
          <Button color="inherit" size="small" onClick={() => { close(); navigate(denied?.upgradePath || '/subscription'); }}>
            {t('See plans', { defaultValue: 'See plans' })}
          </Button>
        ) : undefined}
        sx={{ width: '100%' }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default EntitlementSnackbar;
